import {
	formatIstDate,
	formatMobileNumber,
	optionalEmailSchema,
	parseTimestamp,
	profileUpdateSchema,
	USER_TYPE_LABELS,
} from "@anthra/platform/domain"
import { toDisplayable } from "@anthra/platform/errors"
import { Button } from "@anthra/ui/button"
import { buttonVariants } from "@anthra/ui/button-variants"
import { cn } from "@anthra/ui/cn"
import { controlClasses } from "@anthra/ui/control"
import {
	Dialog,
	DialogContent,
	DialogDescription,
	DialogFooter,
	DialogHeader,
	DialogTitle,
} from "@anthra/ui/dialog"
import { Skeleton } from "@anthra/ui/skeleton"
import { CardTitle, Description, PageTitle } from "@anthra/ui/typography"
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router"
import { CheckIcon, InfoIcon, LockIcon } from "lucide-react"
import { useEffect, useState } from "react"

import { copy } from "@/copy/profile"
import { useSignOut } from "@/features/auth/use-auth"
import { useProfile, useUpdateProfile } from "@/features/profile/use-profile"
import { useMyQueries } from "@/features/queries/use-queries"
import { useMyVettingRequests } from "@/features/vetting/use-vetting"

/**
 * Profile (US-00-001.4/.18).
 *
 * Built against screens/profile.html: the identity card, the details form, the
 * account facts and the sign-out card, with a confirmation dialog before
 * anything is actually signed out.
 *
 * FULL NAME AND EMAIL ARE EDITABLE. The mobile number is rendered read-only and
 * the User Type has NO EDIT CONTROL AT ALL — `profileUpdateSchema` is `.strict()`,
 * so a PATCH carrying either fails before it is sent and the server answers 400
 * to anything that bypasses this screen.
 *
 * WHAT THE DESIGN DRAWS THAT THIS DOES NOT: a 375px tap-to-edit list inside a
 * fixed-width phone frame, which the bundle's own comment calls a review aid
 * rather than a second layout to ship. The real narrow-viewport experience is
 * this same form, reflowed by the shell, the way every screen works. The
 * "Queries raised"/"Vetting requests" facts also drop the design's "1 awaiting
 * your reply" / "both reviewed" qualifier — `QuerySummary` and
 * `VettingSummary` carry no field that answers that, only a total count, and
 * inventing one here would be a number nobody's request produced.
 */
export const Route = createFileRoute("/_app/profile")({
	component: Profile,
})

function initials(fullName: string): string {
	const parts = fullName.trim().split(/\s+/).filter(Boolean)
	const first = parts.at(0) ?? ""
	const last = parts.length > 1 ? (parts.at(-1) ?? "") : ""
	return (first.slice(0, 1) + last.slice(0, 1)).toUpperCase()
}

function Profile() {
	const navigate = useNavigate()
	const profile = useProfile()
	const update = useUpdateProfile()
	const signOut = useSignOut()
	const queries = useMyQueries({}, 1)
	const vetting = useMyVettingRequests({}, 1)

	const [fullName, setFullName] = useState("")
	const [email, setEmail] = useState("")
	const [errors, setErrors] = useState<Record<string, string>>({})
	const [saved, setSaved] = useState(false)
	const [confirmingSignOut, setConfirmingSignOut] = useState(false)

	const user = profile.data

	useEffect(() => {
		if (!user) return
		setFullName(user.fullName)
		setEmail(user.email ?? "")
	}, [user])

	if (profile.isPending) return <ProfileSkeleton />
	if (profile.isError || !user) {
		const displayable = toDisplayable(profile.error)
		return (
			<div data-screen="profile">
				<div className="flex flex-col gap-1">
					<h1 className="text-page-title text-foreground">Profile</h1>
				</div>
				<div
					className="rounded-card border-status-breach-border bg-status-breach-bg mx-[var(--space-card-padding)] flex items-start gap-3 border p-4"
					role="alert"
				>
					<InfoIcon
						className="size-5 shrink-0"
						aria-hidden="true"
					/>
					<div className="flex flex-col gap-2">
						<p>
							<strong>{displayable.title}</strong> {displayable.message}
						</p>
						<div className="flex flex-wrap gap-2">
							<button
								type="button"
								className={cn(buttonVariants({ variant: "secondary" }))}
								onClick={() => {
									void profile.refetch()
								}}
							>
								Try again
							</button>
						</div>
					</div>
				</div>
			</div>
		)
	}

	// Re-bound, narrowed, AFTER the guard above: a nested `function` declaration
	// does not inherit the outer `user`'s narrowing from an `if (!user) return`
	// several lines above it, so `discard` closing over the wider `User |
	// undefined` was a real `tsc` error, not a lint nag.
	const currentUser = user

	function save(): void {
		const emailResult = optionalEmailSchema.safeParse(email)
		if (!emailResult.success) {
			setErrors({ email: emailResult.error.issues[0]?.message ?? "Invalid email." })
			return
		}

		const parsed = profileUpdateSchema.safeParse({ fullName, email })
		if (!parsed.success) {
			const fieldErrors: Record<string, string> = {}
			for (const issue of parsed.error.issues) {
				const field = issue.path[0]
				if (typeof field === "string") fieldErrors[field] ??= issue.message
			}
			setErrors(fieldErrors)
			return
		}

		setErrors({})
		update.mutate(
			{ fullName, email },
			{
				onSuccess: () => {
					setSaved(true)
				},
			},
		)
	}

	const memberSince = parseTimestamp(user.createdAt)

	function discard(): void {
		setFullName(currentUser.fullName)
		setEmail(currentUser.email ?? "")
		setErrors({})
		setSaved(false)
	}

	return (
		<div data-screen="profile">
			<div className="flex flex-col gap-6">
				<div className="flex flex-col gap-1">
					<PageTitle>Profile</PageTitle>
					<Description>
						Your name and email are yours to change. Your number and user type are fixed
						to the account.
					</Description>
				</div>

				{/* A MEASURE. This was a `grid` with no columns — a single column at
				    the full page width — so every card, every label and every
				    read-only row stretched to 930px at 1440, and the locked rows'
				    `justify-between` pushed a lock icon and a value to opposite
				    ends of it. `--width-content` is the design's own reading
				    measure and had no consumer on this screen. */}
				<div className="grid max-w-[var(--width-content)] min-w-0 gap-4">
					<div className="rounded-card border-border bg-card shadow-card flex flex-col gap-4 border p-4">
						<div className="flex items-center gap-4">
							<span
								/* `font-family-ui` because `text-section-title` is being used here
								   as a SIZE for a circle, not to set a heading — and the role
								   now carries the prose face, so without this the initials
								   render in the serif. An explicit family utility outranks the
								   role binding; see kit.css. */
								className="bg-avatar-bg text-avatar-text text-section-title font-family-ui rounded-circle flex size-14 shrink-0 items-center justify-center"
								aria-hidden="true"
							>
								{initials(user.fullName)}
							</span>
							<span className="flex min-w-0 flex-col gap-1">
								<strong className="text-section-title text-foreground">
									{user.fullName}
								</strong>
								<span className="text-meta text-muted flex flex-wrap items-center gap-2">
									<span className="tabular-nums">
										{formatMobileNumber(user.mobileNumber)}
									</span>
									<span className="rounded-pill border-border bg-subtle text-subtle-foreground text-meta inline-flex w-fit items-center gap-1.5 border px-2.5 py-1">
										<span
											className="rounded-circle size-[var(--size-status-dot)] bg-current"
											aria-hidden="true"
										/>
										{USER_TYPE_LABELS[user.userType]}
									</span>
								</span>
							</span>
						</div>
					</div>

					<form
						noValidate
						className="rounded-card border-border bg-card shadow-card flex flex-col gap-4 border p-4"
						onSubmit={event => {
							event.preventDefault()
							save()
						}}
						onReset={event => {
							event.preventDefault()
							discard()
						}}
					>
						<div className="flex flex-col gap-1">
							<h2 className="text-card-title text-foreground">Your details</h2>
							<p className="text-description text-subtle-foreground">
								Experts see this name on your queries and vetting requests.
							</p>
						</div>

						<div className="flex flex-col gap-4">
							<div className="flex flex-col gap-1.5">
								<label htmlFor="d-name">Full name</label>
								<input
									className={cn(controlClasses, "h-11")}
									id="d-name"
									name="fullName"
									autoComplete="name"
									value={fullName}
									aria-invalid={errors.fullName !== undefined}
									onChange={event => {
										setSaved(false)
										setFullName(event.target.value)
									}}
								/>
								{errors.fullName === undefined ? null : (
									<p
										role="alert"
										className="text-description text-destructive font-medium"
									>
										{errors.fullName}
									</p>
								)}
							</div>

							<div className="flex flex-col gap-1.5">
								<label htmlFor="d-email">
									Email <span className="text-muted font-normal">(optional)</span>
								</label>
								<input
									className={cn(controlClasses, "h-11")}
									id="d-email"
									name="email"
									type="email"
									autoComplete="email"
									value={email}
									aria-describedby="d-email-hint"
									aria-invalid={errors.email !== undefined}
									onChange={event => {
										setSaved(false)
										setEmail(event.target.value)
									}}
								/>
								<p
									className="text-meta text-muted"
									id="d-email-hint"
								>
									We use this only to send you a copy of an expert&rsquo;s reply.
									OTPs always arrive on WhatsApp.
								</p>
								{errors.email === undefined ? null : (
									<p
										role="alert"
										className="text-description text-destructive font-medium"
									>
										{errors.email}
									</p>
								)}
							</div>

							<div className="flex flex-col gap-1.5">
								<span
									className="text-field-label text-foreground"
									id="d-mobile-label"
								>
									WhatsApp number
								</span>
								<div
									className="rounded-control border-border bg-subtle flex items-center justify-between gap-3 border px-3 py-2"
									role="group"
									aria-labelledby="d-mobile-label"
									aria-describedby="d-mobile-hint"
								>
									<span
										className="text-muted size-4 shrink-0"
										aria-hidden="true"
									>
										<LockIcon
											className="size-5 shrink-0"
											aria-hidden="true"
										/>
									</span>
									<span className="text-body text-foreground tabular-nums">
										{formatMobileNumber(user.mobileNumber)}
									</span>
									<span className="rounded-pill border-status-answered-border bg-status-answered-bg text-status-answered-fg text-meta inline-flex w-fit items-center border px-2.5 py-1">
										Verified
									</span>
								</div>
								<p
									className="text-meta text-muted"
									id="d-mobile-hint"
								>
									This is the number your sign-in OTP goes to, so it can&rsquo;t
									be edited here. To move your account to a new number,{" "}
									<Link to="/queries/new">raise a query</Link> and an expert will
									do it with you.
								</p>
							</div>

							<div className="flex flex-col gap-1.5">
								<span
									className="text-field-label text-foreground"
									id="d-type-label"
								>
									User type
								</span>
								<div
									className="rounded-control border-border bg-subtle flex items-center justify-between gap-3 border px-3 py-2"
									role="group"
									aria-labelledby="d-type-label"
									aria-describedby="d-type-hint"
								>
									<span
										className="text-muted size-4 shrink-0"
										aria-hidden="true"
									>
										<LockIcon
											className="size-5 shrink-0"
											aria-hidden="true"
										/>
									</span>
									<span className="text-body text-foreground">
										<span className="rounded-pill border-border bg-subtle text-subtle-foreground text-meta inline-flex w-fit items-center border px-2.5 py-1">
											{USER_TYPE_LABELS[user.userType]}
										</span>
									</span>
								</div>
								<p
									className="text-meta text-muted"
									id="d-type-hint"
								>
									Chosen at registration. Ask an expert if it is wrong.
								</p>
							</div>
						</div>

						{update.isError ? (
							<div
								className="rounded-card border-status-breach-border bg-status-breach-bg mx-[var(--space-card-padding)] flex items-start gap-3 border p-4"
								role="alert"
							>
								<InfoIcon
									className="size-5 shrink-0"
									aria-hidden="true"
								/>
								<div className="flex flex-col gap-2">
									<p>
										<strong>We couldn&rsquo;t save your changes.</strong>{" "}
										{toDisplayable(update.error).message}
									</p>
									<div className="flex flex-wrap gap-2">
										<button
											type="button"
											className={cn(buttonVariants({ variant: "secondary" }))}
											onClick={save}
										>
											Try saving again
										</button>
									</div>
								</div>
							</div>
						) : null}
						{saved && !update.isError ? (
							<div
								className="rounded-card border-status-answered-border bg-status-answered-bg flex items-start gap-2 border p-3"
								role="status"
							>
								<CheckIcon
									className="size-5 shrink-0"
									aria-hidden="true"
								/>
								<div className="flex flex-col gap-2">
									<p>
										<strong>Saved.</strong> Experts will see this on your next
										query.
									</p>
								</div>
							</div>
						) : null}

						<div className="border-border flex flex-wrap items-center gap-2 border-t pt-4">
							<button
								type="submit"
								className={cn(buttonVariants({ variant: "primary" }))}
								disabled={update.isPending}
							>
								{update.isPending ? "Saving…" : "Save changes"}
							</button>
							<button
								type="reset"
								className={cn(buttonVariants({ variant: "ghost" }))}
							>
								Discard changes
							</button>
						</div>
					</form>
				</div>

				{/* THE SAME MEASURE AS "Your details" ABOVE. That form sits in a
				    `max-w-[var(--width-content)]` wrapper and these two did not, so
				    Account and This device ran the full 968px of the page while the
				    section above them stopped at 704 — three stacked cards in one
				    column with two different right edges. */}
				<div className="flex max-w-[var(--width-content)] flex-col gap-4">
					<div className="rounded-card border-border bg-card shadow-card flex flex-col gap-4 border p-4">
						<div className="flex flex-col gap-1">
							<h2 className="text-card-title text-foreground">Account</h2>
						</div>
						<div className="flex flex-col gap-4">
							<dl className="flex flex-col gap-3">
								<div className="flex flex-wrap items-baseline justify-between gap-2">
									<dt>Member since</dt>
									<dd className="tabular-nums">
										{memberSince ? formatIstDate(memberSince) : "—"}
									</dd>
								</div>
								<div className="flex flex-wrap items-baseline justify-between gap-2">
									<dt>Queries raised</dt>
									<dd>
										<Link to="/queries">
											{queries.data?.pagination.totalCount ?? 0} queries
										</Link>
									</dd>
								</div>
								<div className="flex flex-wrap items-baseline justify-between gap-2">
									<dt>Vetting requests</dt>
									<dd>
										<Link to="/vetting">
											{vetting.data?.pagination.totalCount ?? 0} requests
										</Link>
									</dd>
								</div>
							</dl>
						</div>
					</div>

					<div className="rounded-card border-border bg-card shadow-card flex flex-col gap-4 border p-4">
						<div className="flex flex-col gap-1">
							<h2 className="text-card-title text-foreground">This device</h2>
							<p className="text-description text-subtle-foreground">
								Your session stays open while you use it and ends after 5 days of no
								activity.
							</p>
						</div>
						<div className="flex flex-col gap-4">
							<button
								className={cn(buttonVariants({ variant: "danger" }), "w-full")}
								type="button"
								onClick={() => {
									setConfirmingSignOut(true)
								}}
							>
								<svg
									width="18"
									height="18"
									viewBox="0 0 24 24"
									fill="none"
									stroke="currentColor"
									strokeWidth="2"
									strokeLinecap="round"
									strokeLinejoin="round"
									aria-hidden="true"
								>
									<path d="M15 17l5-5-5-5M20 12H9M12 21H6a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h6" />
								</svg>
								Sign out
							</button>
							<div
								className="rounded-card border-status-in-progress-border bg-status-in-progress-bg flex items-start gap-2 border p-3"
								role="note"
							>
								<InfoIcon
									className="size-5 shrink-0"
									aria-hidden="true"
								/>
								<div className="flex flex-col gap-2">
									<p>{copy.default.muted5}</p>
								</div>
							</div>
						</div>
					</div>
				</div>
			</div>

			<Dialog
				open={confirmingSignOut}
				onOpenChange={setConfirmingSignOut}
			>
				<DialogContent overlayClassName="dialog-stage">
					<DialogHeader>
						<DialogTitle asChild>
							<CardTitle as="h2">Sign out of Swamithra?</CardTitle>
						</DialogTitle>
						<DialogDescription asChild>
							<Description>
								Your queries and documents stay where they are. Signing back in
								takes your number and a new 6-digit OTP.
							</Description>
						</DialogDescription>
					</DialogHeader>
					<DialogFooter>
						<Button
							variant="secondary"
							onClick={() => {
								setConfirmingSignOut(false)
							}}
						>
							Stay signed in
						</Button>
						<Button
							variant="danger"
							disabled={signOut.isPending}
							onClick={() => {
								signOut.mutate(undefined, {
									onSettled: () => {
										void navigate({ to: "/entry", replace: true })
									},
								})
							}}
						>
							{signOut.isPending ? "Signing out…" : "Sign out"}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>
		</div>
	)
}

/**
 * Loading (US-00-001.4). Skeletons take the shape of the record that is
 * coming — avatar, two editable fields, two locked rows — so the layout does
 * not shift once it arrives (screens/profile.html state 2).
 */
function ProfileSkeleton() {
	return (
		<div
			data-screen="profile"
			aria-busy="true"
		>
			<p
				role="status"
				className="sr-only"
			>
				Loading your profile.
			</p>
			<div className="flex flex-col gap-6">
				<div className="flex flex-col gap-1">
					<h1 className="text-page-title text-foreground">Profile</h1>
				</div>
				{/* A MEASURE. This was a `grid` with no columns — a single column at
				    the full page width — so every card, every label and every
				    read-only row stretched to 930px at 1440, and the locked rows'
				    `justify-between` pushed a lock icon and a value to opposite
				    ends of it. `--width-content` is the design's own reading
				    measure and had no consumer on this screen. */}
				<div className="grid max-w-[var(--width-content)] min-w-0 gap-4">
					<div className="rounded-card border-border bg-card shadow-card flex flex-col gap-4 border p-4">
						<div className="flex items-center gap-4">
							<Skeleton className="rounded-circle size-14" />
							{/* The inline style set `display:grid` over a className that
							    said `flex flex-col`, and restated a gap the className
							    already gave. Two rules for one stack. */}
							<span className="flex min-w-0 flex-col gap-1">
								<Skeleton className="h-4 w-1/2" />
								<Skeleton className="h-4 w-1/3" />
							</span>
						</div>
					</div>
					<div className="rounded-card border-border bg-card shadow-card flex flex-col gap-4 border p-4">
						<div className="flex flex-col gap-1">
							<Skeleton className="h-5 w-2/5" />
						</div>
						<div className="flex flex-col gap-4">
							<Skeleton className="h-11 w-full" />
							<Skeleton className="h-11 w-full" />
							<Skeleton className="h-4 w-full" />
							<Skeleton className="h-4 w-full" />
						</div>
					</div>
				</div>
				<div className="flex flex-col gap-4">
					<div className="rounded-card border-border bg-card shadow-card flex flex-col gap-4 border p-4">
						<div className="flex flex-col gap-1">
							<Skeleton className="h-4 w-full" />
						</div>
						<div className="flex flex-col gap-4">
							<Skeleton className="h-4 w-full" />
							<Skeleton className="h-4 w-full" />
						</div>
					</div>
					<div className="rounded-card border-border bg-card shadow-card flex flex-col gap-4 border p-4">
						<div className="flex flex-col gap-1">
							<Skeleton className="h-4 w-full" />
						</div>
						<div className="flex flex-col gap-4">
							<Skeleton className="h-11 w-full" />
						</div>
					</div>
				</div>
			</div>
		</div>
	)
}
